import { Vue, Component } from 'vue-property-decorator';
import moment, { Moment } from 'moment';
import { ALL_TRANSLATIONS } from '../translations/all';
import {
  ReservationEntity,
  LocationEntity,
  EntranceEntity,
  TimeslotEntity,
  WebviewQueryStringParameters,
  OccupantTypeEntity,
  TranslationLanguage,
  GetExternalUserReservationsOptions,
  LocationVisualSettings,
  CurrentCheckIn,
  ScanEntity,
  VisitTypeEntity,
} from '../../../types';
import { parseQueryParameters } from '@/helpers';

@Component
export default class GlobalMixIn extends Vue {
  // Data Properties

  public fullScreenLoader = null as any;

  // Methods

  public moment = moment;

  public getLatestData = async () => {
    this.$store.commit('appData/REMOVE_EXISTING_DATA');

    if (this.queryParameters.qrCode) {
      await this.$store.dispatch('appData/GET_RESERVATION', this.queryParameters.qrCode);
    } else if (this.queryParameters.locationId !== null) {
      await this.$store.dispatch('appData/GET_LOCATION_APP_DATA', {
        locationId: this.queryParameters.locationId,
        includeOccupantTypes: true,
        includeLocation: true,
      });

      if (this.externalUserId !== null && this.customerId !== null) {
        const externalUserReservationsOptions: GetExternalUserReservationsOptions = {
          customerId: this.customerId,
          externalUserId: this.externalUserId,
        };

        await this.$store.dispatch('appData/GET_EXTERNAL_USER_RESERVATIONS', externalUserReservationsOptions);
      }
    } else {
      await this.$store.dispatch('appData/GET_CUSTOMER_APP_DATA');

      if (this.locations.length === 1) {
        await this.$store.dispatch('appData/GET_LOCATION_APP_DATA', {
          locationId: this.locations[0].id,
          includeOccupantTypes: false,
          includeLocation: true,
        });
      }
    }
  };

  public hideLoader = () => {
    if (this.fullScreenLoader !== null) {
      this.fullScreenLoader.hide();
    }
  };

  public showLoader = () => {
    this.fullScreenLoader = (this as any).$loading.show({
      container: null,
      loader: 'spinner',
      color: this.primaryColor,
      width: 100,
      height: 100,
    });
  };

  public getLocationData = (
    locationId: number,
    includeOccupantTypes: boolean,
    includeLocation: boolean,
    visitTypeId: number | null = null,
  ): Promise<void> => {
    return this.$store.dispatch('appData/GET_LOCATION_APP_DATA', {
      locationId,
      visitTypeId,
      includeOccupantTypes,
      includeLocation,
    });
  };

  public getCustomerData = (): Promise<void> => {
    return this.$store.dispatch('appData/GET_CUSTOMER_APP_DATA');
  };

  public startLoading = () => {
    this.$store.commit('appData/SET_LOADING', true);

    this.showLoader();
  };

  public stopLoading = () => {
    this.$store.commit('appData/SET_LOADING', false);

    this.hideLoader();
  };

  public formatMomentWithLocale = (momentObject: Moment, formatString: string, overrideLocale = false) => {
    const localeToUse = overrideLocale ? 'en' : this.$root.$i18n.locale;

    return momentObject.locale(localeToUse).format(formatString);
  };

  public setLocale = (locale: string) => {
    if (this.$root.$i18n.availableLocales.includes(locale)) {
      this.$root.$i18n.locale = locale;
    }
  };

  public getLocationName = (location: LocationEntity): string => {
    return this.$root.$i18n.locale === 'en'
      ? location.name
      : location.nameTranslations && location.nameTranslations[this.$root.$i18n.locale]
      ? location.nameTranslations[this.$root.$i18n.locale]
      : location.name;
  };

  public setTranslations = (translations: TranslationLanguage[] | null, languageOverride?: string) => {
    const defaultTranslations = JSON.parse(JSON.stringify(ALL_TRANSLATIONS));

    if (typeof translations === 'undefined' || translations === null) {
      Object.keys(defaultTranslations).forEach((langCode: string) => {
        this.$root.$i18n.setLocaleMessage(langCode, (defaultTranslations as any)[langCode]);
      });

      this.setLocale(languageOverride || 'en');

      return;
    }

    const defaultLanguageObject = translations.find((translation) => translation.default);

    const defaultLanguage = defaultLanguageObject ? defaultLanguageObject.langCode : 'en';

    translations.forEach((translation) => {
      const combinedTranslations = Object.assign({}, (defaultTranslations as any)[translation.langCode] || {}, translation.phrases);

      this.$root.$i18n.setLocaleMessage(translation.langCode, combinedTranslations);
    });

    this.setLocale(languageOverride || defaultLanguage);
  };

  public setVisualSettings = (visualSettings: LocationVisualSettings | null): void => {
    if (visualSettings !== null) {
      const newQueryParameters = Object.assign(this.queryParameters, visualSettings);

      this.$store.commit('appData/SET_QUERY_PARAMETERS', newQueryParameters);
    }
  };

  public updateQueryParameters() {
    const existingQueryParameters = this.$store.getters['appData/QUERY_PARAMETERS'];
    const newQueryParameters = this.$route.query;

    const queryParameters =
      Object.keys(newQueryParameters).length === 0 ? parseQueryParameters(existingQueryParameters) : parseQueryParameters(newQueryParameters);

    (this as any).setLocale(queryParameters.lang);

    this.$store.commit('appData/SET_QUERY_PARAMETERS', queryParameters);
  }

  // Computed Properties

  get locations(): LocationEntity[] {
    return this.$store.getters['appData/LOCATIONS'];
  }

  get entrances(): EntranceEntity[] {
    return this.$store.getters['appData/ENTRANCES'];
  }

  get timeslots(): TimeslotEntity[] {
    return this.$store.getters['appData/TIMESLOTS'];
  }

  get occupantTypes(): OccupantTypeEntity[] {
    return this.$store.getters['appData/OCCUPANT_TYPES'];
  }

  get visitTypes(): VisitTypeEntity[] {
    return this.$store.getters['appData/VISIT_TYPES'];
  }

  get reservation(): ReservationEntity {
    return this.$store.getters['appData/RESERVATION'];
  }

  get externalUserReservations(): ReservationEntity[] {
    return this.$store.getters['appData/EXTERNAL_USER_RESERVATIONS'];
  }

  get externalUserName(): ReservationEntity[] {
    return this.$store.getters['appData/EXTERNAL_USER_NAME'];
  }

  get externalUserEmail(): ReservationEntity[] {
    return this.$store.getters['appData/EXTERNAL_USER_EMAIL'];
  }

  get externalUserPhone(): ReservationEntity[] {
    return this.$store.getters['appData/EXTERNAL_USER_PHONE'];
  }

  get externalUserOccupantTypeId(): number | null {
    return this.$store.getters['appData/EXTERNAL_USER_OCCUPANT_TYPE_ID'];
  }

  get externalUserAPIKeyUsed(): number | null {
    return this.$store.getters['appData/EXTERNAL_USER_API_KEY_USED'];
  }

  get queryParameters(): WebviewQueryStringParameters {
    return this.$store.getters['appData/QUERY_PARAMETERS'];
  }

  get globalLoading(): boolean {
    return this.$store.getters['appData/LOADING'];
  }

  get currentCheckIn(): CurrentCheckIn {
    return this.$store.getters['appData/CURRENT_CHECK_IN'];
  }

  get currentCheckInScan(): ScanEntity | null {
    return this.currentCheckIn.scan;
  }

  get currentCheckInLocation(): LocationEntity | null {
    return this.currentCheckIn.location;
  }

  get currentCheckInReservation(): ReservationEntity | null {
    return this.currentCheckIn.reservation;
  }

  get currentCheckInExternalUserId(): string | null {
    return this.currentCheckIn.externalUserId;
  }

  get currentCheckInAutoCheckOutTimestamp(): number | null {
    return this.currentCheckIn.autoCheckOutTimestamp;
  }

  get checkedIn(): boolean {
    return (
      this.currentCheckInScan !== null &&
      (this.currentCheckInAutoCheckOutTimestamp === null || +new Date() < this.currentCheckInAutoCheckOutTimestamp)
    );
  }

  // Entities

  get occupantType(): OccupantTypeEntity | null {
    return this.occupantTypes.find((occupantType) => occupantType.id === this.occupantTypeId) || null;
  }

  get visitType(): VisitTypeEntity | null {
    return this.visitTypes.find((visitType) => visitType.id === this.visitTypeId) || null;
  }

  // Query Parameters

  get apiKey(): string | null {
    return this.queryParameters.apiKey;
  }

  get customerId(): number | null {
    return this.queryParameters.customerId;
  }

  get locationId(): number | null {
    return this.queryParameters.locationId;
  }

  get occupantTypeId(): number | null {
    return this.externalUserOccupantTypeId || this.queryParameters.occupantTypeId;
  }

  get visitTypeId(): number | null {
    return this.queryParameters.visitTypeId;
  }

  get fcmToken(): string | null {
    return this.queryParameters.fcmToken;
  }

  get queryParametersName(): string | null {
    return this.queryParameters.name;
  }

  get queryParametersEmail(): string | null {
    return this.queryParameters.email;
  }

  get externalUserId(): string | null {
    return this.queryParameters.externalUserId;
  }

  get webview(): boolean {
    return this.queryParameters.webview;
  }

  get userLatitude(): number | null {
    return this.queryParameters.userLatitude;
  }

  get userLongitude(): number | null {
    return this.queryParameters.userLongitude;
  }

  get lang(): string {
    return this.queryParameters.lang;
  }

  get widgetLayout(): '1-col' | '1-col-short' | '1-col-condensed' | '2-col' | '3-col' | null {
    return this.queryParameters.widgetLayout;
  }

  get qrCode(): string | null {
    return this.queryParameters.qrCode;
  }

  get cssURL(): string | null {
    return this.queryParameters.cssURL;
  }

  get primaryColor(): string | null {
    return this.queryParameters.primaryColor;
  }

  get primaryFontColor(): string | null {
    return this.queryParameters.primaryFontColor;
  }

  get primaryButtonFontColor(): string | null {
    return this.queryParameters.primaryButtonFontColor;
  }

  get secondColor(): string | null {
    return this.queryParameters.secondColor;
  }

  get secondFontColor(): string | null {
    return this.queryParameters.secondFontColor;
  }

  get secondButtonFontColor(): string | null {
    return this.queryParameters.secondButtonFontColor;
  }

  get thirdColor(): string | null {
    return this.queryParameters.thirdColor;
  }

  get thirdFontColor(): string | null {
    return this.queryParameters.thirdFontColor;
  }

  get thirdButtonFontColor(): string | null {
    return this.queryParameters.thirdButtonFontColor;
  }

  get fourthColor(): string | null {
    return this.queryParameters.fourthColor;
  }

  get fourthFontColor(): string | null {
    return this.queryParameters.fourthFontColor;
  }

  get fourthButtonFontColor(): string | null {
    return this.queryParameters.fourthButtonFontColor;
  }

  get headerBackgroundColor(): string | null {
    return this.queryParameters.headerBackgroundColor;
  }

  get languagePickerColor(): string | null {
    return this.queryParameters.languagePickerColor;
  }
}
